// @oasis-permission-inherits: /capcode (capcode:view)
import { Icon } from "@mirage-web/icons";
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { getTestReportQuery } from "~/modules/test-runs/queries";
import {
  ARTIFACT_TYPE_LABELS,
  TEST_CASE_TYPE_LABELS,
  type Artifact,
  type TestCase,
} from "~/modules/test-runs/schemas";

const CAPTIONS_WHISPER_COMMIT_URL = "https://github.com/bungeeapp/captions-whisper/commit";

const searchSchema = z.object({
  commitHash: z.string().optional(),
  tag: z.string().optional(),
});

export const Route = createFileRoute("/_dash/capcode/test-runs/$reportId")({
  validateSearch: searchSchema,
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;
    await queryClient.prefetchQuery(
      getTestReportQuery.queryOptions({
        pathParams: { reportId: params.reportId },
      })
    );
  },
  pendingComponent: PendingComponent,
});

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
}

function formatDuration(ms: number | null | undefined) {
  if (ms == null) {
    return "—";
  }
  if (ms < 1000) {
    return `${ms} ms`;
  }
  return `${(ms / 1000).toFixed(2)} s`;
}

function PendingComponent() {
  const search = Route.useSearch();
  return (
    <div className="space-y-6">
      <Link
        to="/capcode/test-runs"
        search={search}
        className="text-sm text-muted-foreground hover:underline"
      >
        &larr; Back to Test Runs
      </Link>
      <Skeleton className="h-10 w-96 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
    </div>
  );
}

function RouteComponent() {
  const { reportId } = Route.useParams();
  const search = Route.useSearch();

  const { data: report } = getTestReportQuery.useSuspenseQuery({
    pathParams: { reportId },
  });

  const hasFailures = report.failedCount > 0;

  return (
    <div className="space-y-6">
      <Link
        to="/capcode/test-runs"
        search={search}
        className="text-sm text-muted-foreground hover:underline"
      >
        &larr; Back to Test Runs
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>{report.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Report ID:</span>
            <code className="text-xs">{report.id}</code>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => copyToClipboard(report.id)}
            >
              Copy
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Commit:</span>
            <a
              href={`${CAPTIONS_WHISPER_COMMIT_URL}/${report.commitHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-xs text-blue-600 hover:underline"
            >
              {report.commitHash.slice(0, 12)}
            </a>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => copyToClipboard(report.commitHash)}
            >
              Copy
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Created At:</span>
            <span>{new Date(report.createdAt).toLocaleString()}</span>
          </div>
          {report.description && (
            <div>
              <span className="text-muted-foreground">Description:</span>
              <p className="mt-1 whitespace-pre-wrap">{report.description}</p>
            </div>
          )}
          {report.tags && report.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-muted-foreground">Tags:</span>
              {report.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
          {report.metadata && Object.keys(report.metadata).length > 0 && (
            <div>
              <span className="text-muted-foreground">Metadata:</span>
              <pre className="mt-1 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">
                {JSON.stringify(report.metadata, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-sm">
              Total: {report.totalCount}
            </Badge>
            <Badge
              variant={hasFailures ? "outline" : "default"}
              className="text-sm border-emerald-500/60 text-emerald-700 dark:text-emerald-300"
            >
              Passed: {report.passedCount}
            </Badge>
            <Badge variant={hasFailures ? "destructive" : "outline"} className="text-sm">
              Failed: {report.failedCount}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test Cases ({report.testCases.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[44%]">Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Tags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.testCases.map((testCase, index) => (
                <TestCaseRow key={`${testCase.name}-${index}`} testCase={testCase} />
              ))}
              {report.testCases.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No test cases recorded
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function TestCaseRow({ testCase }: { testCase: TestCase }) {
  const hasArtifacts = testCase.artifacts.length > 0;
  const hasFailureMessage = Boolean(testCase.result.failureMessage);
  const expandable = hasArtifacts || hasFailureMessage;

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="font-medium">{testCase.name}</span>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="text-xs">
            {TEST_CASE_TYPE_LABELS[testCase.type]}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge variant={testCase.result.passed ? "default" : "destructive"} className="text-xs">
            {testCase.result.passed ? "Passed" : "Failed"}
          </Badge>
        </TableCell>
        <TableCell className="whitespace-nowrap text-xs">
          {formatDuration(testCase.durationMs)}
        </TableCell>
        <TableCell>
          {testCase.tags && testCase.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {testCase.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-[10px]">
                  {tag}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </TableCell>
      </TableRow>
      {expandable && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={5} className="py-2">
            <TestCaseDetails testCase={testCase} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function TestCaseDetails({ testCase }: { testCase: TestCase }) {
  return (
    <div className="space-y-2">
      {testCase.result.failureMessage && (
        <div>
          <div className="text-xs font-medium text-muted-foreground">Failure message</div>
          <pre className="mt-1 whitespace-pre-wrap break-words rounded-md bg-destructive/10 p-3 text-xs text-destructive">
            {testCase.result.failureMessage}
          </pre>
        </div>
      )}
      {testCase.artifacts.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs">
              <Icon name="chevron-down" className="size-3" />
              Artifacts ({testCase.artifacts.length})
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 pt-2">
            {testCase.artifacts.map((artifact, index) => (
              <ArtifactRow key={`${artifact.name}-${index}`} artifact={artifact} />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

function ArtifactRow({ artifact }: { artifact: Artifact }) {
  return (
    <div className="rounded-md border bg-muted/40 p-2">
      <div className="flex items-center gap-2 text-xs">
        <Badge variant="outline" className="text-[10px]">
          {ARTIFACT_TYPE_LABELS[artifact.type]}
        </Badge>
        <span className="font-medium">{artifact.name}</span>
        {artifact.assetId && (
          <>
            <span className="text-muted-foreground">·</span>
            <code className="text-[10px] text-muted-foreground">{artifact.assetId}</code>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1 text-[10px]"
              onClick={() => copyToClipboard(artifact.assetId!)}
            >
              Copy
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
